import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { useCommonGameSet } from '@sorare/core/src/set';

import type { MissionCollectionCard_cardCollection } from './MissionCollectionCard/__generated__/index.graphql.ts';
import { MissionCollectionCard } from './MissionCollectionCard/index.tsx';
import type {
  TaskCollectionsQuery,
  TaskCollectionsQueryVariables,
  TaskCollections_taskInterface,
} from './__generated__/index.graphql.ts';

const TASK_COLLECTIONS_QUERY = gql`
  query TaskCollectionsQuery(
    $sport: Sport!
    $cardSetSlug: String
    $forCompetitions: [String!]
    $teamSlugs: [String!]
    $rarities: [Rarity!]
    $user: String!
    $after: String
    $inSeasonOnly: Boolean
  ) {
    currentUser {
      slug
      cardCollections(
        sport: $sport
        sortType: HIGHEST_SCORE
        first: 6
        after: $after
        cardSetSlug: $cardSetSlug
        forCompetitions: $forCompetitions
        teamSlugs: $teamSlugs
        inSeasonOnly: $inSeasonOnly
        rarities: $rarities
      ) {
        nodes {
          slug
          ...MissionCollectionCard_cardCollection
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${MissionCollectionCard.fragments.cardCollection}
` as TypedDocumentNode<TaskCollectionsQuery, TaskCollectionsQueryVariables>;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  gap: var(--unit);
`;
type Props = {
  task: TaskCollections_taskInterface;
};

export const TaskCollections = ({ task }: Props) => {
  const { sport } = useSportContext();
  const { slug: cardSetSlug } = useCommonGameSet();
  const { currentUser } = useCurrentUserContext();
  const isCollectionsTask = isType(task, 'CompleteCollectionsCountTargetTask');

  const { data, loadMore, networkStatus } = usePaginatedQuery(
    TASK_COLLECTIONS_QUERY,
    {
      variables: {
        sport,
        ...(task.rarity === Rarity.common
          ? { cardSetSlug }
          : {
              forCompetitions: isType(
                task,
                'CompleteCollectionsCountTargetTask'
              )
                ? task.competitions.map(c => c.slug)
                : undefined,
              teamSlugs: isType(task, 'CompleteCollectionsCountTargetTask')
                ? task.specifiedTeams.map(t => t.slug)
                : undefined,
              inSeasonOnly: true,
              rarities: task.rarity ? [task.rarity] : undefined,
            }),
        user: currentUser?.slug ?? '',
        after: null,
      },
      connection: 'CardCollectionConnection',
      skip: !isCollectionsTask,
      notifyOnNetworkStatusChange: true,
    }
  );

  if (!isCollectionsTask) {
    return null;
  }

  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const taskSetColor = 'var(--c-nd-600-opaque)';

  const { nodes, pageInfo } = data?.currentUser?.cardCollections ?? {};

  return (
    <Vertical>
      <Horizontal>
        <div style={{ color: taskSetColor }}>
          <Collection size={2} />
        </div>
        <BrandS>
          <FormattedMessage defaultMessage="My collection" />
        </BrandS>
      </Horizontal>
      <Grid>
        {nodes?.map((node: MissionCollectionCard_cardCollection) => (
          <MissionCollectionCard
            key={node.slug}
            cardCollection={node}
            strokeColor={taskSetColor}
          />
        ))}
      </Grid>
      {pageInfo?.hasNextPage ? (
        <Button
          size="medium"
          color="quaternary"
          loading={loadingMore}
          onClick={() => {
            loadMore(false, { after: pageInfo.endCursor });
          }}
        >
          <FormattedMessage {...glossary.viewMore} />
        </Button>
      ) : null}
    </Vertical>
  );
};

TaskCollections.fragments = {
  taskInterface: gql`
    fragment TaskCollections_taskInterface on TaskInterface {
      id
      rarity
      ... on CardsCountTargetTask {
        id
        cardSet {
          slug
          branding {
            id
            colors {
              surfacePrimary
            }
          }
        }
      }
      ... on CompleteCollectionsCountTargetTask {
        id
        competitions {
          slug
        }
        specifiedTeams {
          ... on TeamInterface {
            slug
          }
        }
      }
    }
  ` as TypedDocumentNode<TaskCollections_taskInterface>,
};
